import React from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface ExamplesSlideStackProps {
  showExamples: boolean;
  form: React.ReactNode;
  examples: React.ReactNode;
}

/**
 * EXP-2.2 · One panel, two screens.
 *
 * The item form and the examples editor sit side by side in a stack twice the
 * width of the panel, and the stack translates by half its width. 320ms.
 *
 * ⚠️ BOTH SCREENS STAY MOUNTED. That is the whole reason this is a stack and
 * not a conditional render: an unmounted form loses every uncommitted
 * keystroke and its scroll position, so Back would return to a form that had
 * silently reset. It is also why there is no second slide panel — the
 * precedent here is Manage Template Checklists, which renders inline with a
 * back link rather than stacking panels.
 *
 * ⚠️ `aria-hidden` follows the visible screen, so a screen reader does not
 * read out the form that has slid off to the left, and `inert`-like behaviour
 * is approximated by removing it from the accessibility tree. Under
 * `prefers-reduced-motion` the transition is dropped entirely and everything
 * else is unchanged.
 */
export function ExamplesSlideStack({ showExamples, form, examples }: ExamplesSlideStackProps) {
  const reduced = useReducedMotion();

  return (
    <div className="overflow-hidden">
      <div
        data-examples-stack={showExamples ? 'examples' : 'item'}
        className="flex w-[200%]"
        style={{
          transform: showExamples ? 'translateX(-50%)' : 'translateX(0)',
          transition: reduced ? 'none' : 'transform 320ms cubic-bezier(.16,1,.3,1)',
        }}
      >
        <section className="w-1/2 min-w-0" aria-hidden={showExamples}>
          {form}
        </section>
        <section className="w-1/2 min-w-0" aria-hidden={!showExamples}>
          {/* The examples screen scrolls on its own, so it keeps its place
              independently of wherever the item form is scrolled to. */}
          <div className="max-h-[70vh] overflow-y-auto">{examples}</div>
        </section>
      </div>
    </div>
  );
}
